import React, { useMemo, useState, Suspense, useCallback, useEffect, Fragment, lazy } from "react";
import { IGif, INicheFeedbackResponse, IUser } from "@/common/services/types";
import { MetaInfo } from "../MetaInfo";
import { metricsBus, EventSource } from "@/common/buses";
import { onOpenMoreMenu } from "@/components/popup-dispatcher/PopupDispatcher";
import { useVisitorInfo } from "@/common/hooks/useVisitorInfo";
import { LikeSoundOverlay } from "../LikeSoundOverlay";
import { Sidebar } from "../Sidebar";
import { Player } from "../Player";
import { ImageGif } from "../ImageGif";
import { FullScreenNav } from "../FullScreenNav";
import { RepostInfo } from "../RepostInfo";
import { Vote , TAddFeedback } from "../Vote";
import "./GifPreview.scss";
import { useTagFeedback } from "components/gifPreview/GifPreview/hooks/useTagFeedback";
import { usePlayerSound } from "components/gifPreview/GifPreview/hooks/usePlayerSound";
import { getFeedback } from "@/common/utils/voteHelpers";
import { GifType } from "./constants";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { useLikeActions } from "./hooks/useLikeActions";
import { QuickRecs } from "@/components/gifPreview/QuickRecs";
import { useQuickRecs, CloseType } from "./hooks/useQuickRecs";
import { useSfw } from "@/common/hooks/useSfw";

const GalleryGif = lazy(() => import("@/components/gallery-gif/GalleryGif"));

interface IGifPreviewProps {
  user: IUser | undefined;
  embedded?: boolean;
  rel?: string;
  position: number;
  wrapRef: React.RefObject<HTMLDivElement | null>;
  fullscreen: boolean;
  gif: IGif;
  isVisible: boolean;
  addNicheFeedback?: TAddFeedback;
  nicheFeedbacks?: INicheFeedbackResponse[];
  hideGif: (id: string) => void;
  sendGifView: (gif: IGif) => void;
  playCount: number;
  incrementPlayCount: () => void;
}

export const GifPreview: React.FC<IGifPreviewProps> = (props) => {
  const {
    wrapRef,
    fullscreen,
    isVisible,
    position,
    addNicheFeedback,
    nicheFeedbacks,
    hideGif,
    sendGifView,
    playCount,
    incrementPlayCount,
  } = props;

  const { sanitizeGif, isLoading } = useSfw();
  const { gif } = sanitizeGif(props.gif);

  const [currentGalleryGif, setCurrentGalleryGif] = useState<IGif | null>(gif.gallery ? gif : null);
  const [loop, setLoop] = useState(true);

  const currentUser = useVisitorInfo();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const { liked, handleLike } = useLikeActions({
    gifId: gif.id,
    source: eventSource?.name ?? EventSource.Other,
    sourceId: eventSource?.id ?? null,
    initialLikesCount: gif.likes,
    ref: wrapRef,
    boosted: !!gif.cta,
    position,
    playCount
  });

  const {
    onSendTagFeedback,
    irrelevantTags,
    tagFeedbackLoading,
    tagFeedbackMode,
    onTagFeedbackMode
  } = useTagFeedback({ source: eventSource?.name ?? EventSource.Other, gifId: gif.id });

  const { sound, toggleSound } = usePlayerSound({ ref: wrapRef });

  const {
    gifs: gifsQuickRecs,
    isQuickRecsVisible,
    quickRecsEnabled,
    closeQuickRecs,
    showQuickRecs,
    quickRecsFailed,
  } = useQuickRecs({
    position,
    gif,
    isPlayerVisible: isVisible,
  });

  const onMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, gif: IGif) => {
    onOpenMoreMenu({
      gif,
      anchorEl: event.currentTarget || null,
      position,
      playCount,
      hideGif: hideGif,
      onOpenTagFeedbackMode: () => onTagFeedbackMode(true),
    });
    metricsBus.emit("optionClicked");
  }, [hideGif, eventSource, position, playCount]);

  const getQuickRecsClosedHandler = useCallback((type: CloseType) => () => {
    closeQuickRecs(type);
    setLoop(true);
  }, [closeQuickRecs]);

  const handleVideoEnded = useCallback(() => {
    showQuickRecs();
  }, [showQuickRecs]);

  useEffect(() => {
    if (quickRecsEnabled) {
      setLoop(false);
    }
  }, [quickRecsEnabled]);

  useEffect(() => {
    if (quickRecsFailed) {
      setLoop(true);
    }
  }, [quickRecsFailed]);

  const gifFeedback = useMemo(
    () => nicheFeedbacks ? getFeedback({ gif: gif.id, states: nicheFeedbacks }) : undefined,
    [nicheFeedbacks, gif.id, getFeedback]
  );

  const shouldDisplayTopInfo = !fullscreen && !gif.cta && addNicheFeedback && gifFeedback;

  if (isLoading) {
    return null;
  }

  return (
    <Fragment>
      {shouldDisplayTopInfo && (
        <div className="GifPreview-TopInfo">
          <RepostInfo createDate={gifFeedback.timestamp} userName={gifFeedback.userName} />
          <Vote
            gif={gif.id}
            isVisible={isVisible}
            addNicheFeedback={addNicheFeedback}
            position={position}
            gifFeedback={gifFeedback}
          />
        </div>
      )}

      {fullscreen && <FullScreenNav id={gif.id} />}

      <div className="GifPreview-BackdropWrap">
        <img
          // @ts-expect-error fetchpriority is known attribute
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority={position === 1 ? "high" : undefined}
          src={gif.urls.thumbnail}
          alt={`Poster for ${gif.id}`}
        />
      </div>

      {gif.type === GifType.Video &&
        (
          <Player
            poster={gif.urls.thumbnail}
            position={position}
            gif={gif}
            fullscreen={fullscreen}
            sound={sound}
            handleLike={handleLike}
            isVisible={isVisible}
            isHLS={!!gif.hls}
            onSound={toggleSound}
            currentUserName={currentUser?.username}
            sendGifView={sendGifView}
            playCount={playCount}
            incrementPlayCount={incrementPlayCount}
            onEnded={handleVideoEnded}
            onReplay={getQuickRecsClosedHandler("replay")}
            loop={loop}
          />
        )
      }

      {gif.type === GifType.Image && (
        gif.gallery ? (
          <Suspense>
            <GalleryGif
              galleryId={gif.gallery}
              initialGif={gif}
              setCurrentGalleryGif={setCurrentGalleryGif}
              onLike={handleLike}
              isVisible={isVisible}
              sendGifView={sendGifView}
            />
          </Suspense>
        ) : (
          <ImageGif
            onLike={handleLike}
            gif={gif}
            fullscreen={fullscreen}
            isVisible={isVisible}
            sendGifView={sendGifView}
          />
        )
      )}

      {
        !isQuickRecsVisible && (
          <Fragment>
            <div className="GifPreview-SideBarWrap">
              <Sidebar
                onMenu={onMenu}
                onLike={handleLike}
                onSound={toggleSound}
                gifSound={sound}
                gif={currentGalleryGif !== null && !gif.aliasId ? currentGalleryGif : gif}
                currentUserName={currentUser?.username}
                liked={liked}
                likesCount={gif.likes}
                fullscreen={fullscreen}
                position={position}
                isVisible={isVisible}
                playCount={playCount}
              />
            </div>

            <div className="GifPreview-MetaInfo" onContextMenu={event => event.preventDefault()}>
              <MetaInfo
                gif={currentGalleryGif !== null ? currentGalleryGif : gif}
                tags={gif.tags}
                user={props.user}
                currentUser={currentUser}
                rel={props.rel}
                tagFeedbackMode={tagFeedbackMode}
                irrelevantTags={irrelevantTags}
                tagFeedbackLoading={tagFeedbackLoading}
                onTagFeedbackMode={onTagFeedbackMode}
                onSendTagFeedback={onSendTagFeedback}
                position={position}
                isVisible={isVisible}
                playCount={playCount}
              />
            </div>
          </Fragment>
        )
      }

      {
        isQuickRecsVisible && (
          <QuickRecs
            gifs={gifsQuickRecs}
            rel={props.rel}
            onClose={getQuickRecsClosedHandler("close")}
          />
        )
      }

      <LikeSoundOverlay />
    </Fragment>
  );
};
